import React, { useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import get from 'lodash/get'

import axios from 'lib/axios'
import { formatMoney } from 'lib/numbers'
import { RefundInfoType } from '../../types'

const IssuePaymentRefundForm: React.FC<
  React.PropsWithChildren<{
    onCancel: () => void
    onSubmit: () => void | Promise<void>
    refundInfo: RefundInfoType
  }>
> = ({ onCancel, onSubmit, refundInfo }) => {
  const [refundReason, setRefundReason] = useState('')
  const [confirmedBuildiumRefund, setConfirmedBuildiumRefund] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [reasonTouched, setReasonTouched] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const isBuildiumRefund = refundInfo.paymentSource === 'buildium'
  const trimmedRefundReason = refundReason.trim()
  const isSubmitDisabled =
    isSaving || !trimmedRefundReason || (isBuildiumRefund && !confirmedBuildiumRefund)

  const refundDetails: Array<{ columns?: number; label: string; value: string }> = [
    {
      label: 'Amount',
      value: formatMoney(refundInfo.amount, 2, '$')
    },
    {
      label: 'Payment date',
      value: refundInfo.date
    },
    {
      columns: 2,
      label: 'Address',
      value: refundInfo.address
    }
  ]

  const handleSubmit = async () => {
    setReasonTouched(true)
    setErrorMessage(undefined)

    if (isSubmitDisabled) return

    const shouldSubmit =
      isBuildiumRefund ||
      window.confirm(
        `Are you sure you want to refund this customer for: ${formatMoney(
          refundInfo.amount,
          2,
          '$'
        )}?`
      )

    if (!shouldSubmit) return

    try {
      setIsSaving(true)

      const payload = {
        refund_reason: trimmedRefundReason,
        ...(isBuildiumRefund ? { confirmed_refunded_in_buildium: confirmedBuildiumRefund } : {})
      }

      await axios.post(
        `/admin/payments/${refundInfo.paymentId}/${
          isBuildiumRefund ? 'record_buildium_refund' : 'refund'
        }`,
        payload
      )

      await onSubmit()
    } catch (error) {
      setErrorMessage(
        get(error, 'response.data.message') ||
          (error as Error).message ||
          'Unable to record this refund.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Stack spacing={3}>
      <SimpleGrid
        bg='gray.50'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='sm'
        columns={{ base: 2, md: 4 }}
        p={3}
        spacingX={4}
        spacingY={2}
      >
        {refundDetails.map((detail) => (
          <Box
            gridColumn={{ md: detail.columns ? `span ${detail.columns}` : undefined }}
            key={detail.label}
            minW={0}
          >
            <Text color='gray.500' fontSize='xs' fontWeight='semibold' mb={1}>
              {detail.label}
            </Text>
            <Text fontSize='sm' fontWeight='medium' mb={0} overflowWrap='anywhere'>
              {detail.value}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      {isBuildiumRefund && (
        <Box bg='teal.50' border='1px solid' borderColor='teal.100' borderRadius='sm' p={3}>
          <Text fontSize='sm' fontWeight='semibold' mb={2}>
            Refund and approve this payment in Buildium before recording it in Hudson.
          </Text>
          <Checkbox
            alignItems='flex-start'
            colorScheme='teal'
            isChecked={confirmedBuildiumRefund}
            isDisabled={isSaving}
            onChange={(event) => setConfirmedBuildiumRefund(event.target.checked)}
          >
            <Text fontSize='sm'>
              I have already issued and approved a refund of this payment in Buildium.
            </Text>
          </Checkbox>
        </Box>
      )}

      {errorMessage && (
        <Alert borderRadius='sm' status='error'>
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      <FormControl isInvalid={reasonTouched && !trimmedRefundReason} isRequired>
        <FormLabel fontSize='sm' mb={1}>
          Refund reason
        </FormLabel>
        <Textarea
          isDisabled={isSaving}
          onBlur={() => setReasonTouched(true)}
          onChange={(event) => {
            setRefundReason(event.target.value)
            setErrorMessage(undefined)
          }}
          placeholder='Why was this payment refunded?'
          resize='none'
          rows={2}
          value={refundReason}
        />
        <FormErrorMessage>Enter a refund reason.</FormErrorMessage>
      </FormControl>

      <HStack justify='flex-end' spacing={3}>
        <Button isDisabled={isSaving} onClick={onCancel} variant='ghost'>
          Cancel
        </Button>
        <Button
          colorScheme='teal'
          isDisabled={isSubmitDisabled}
          isLoading={isSaving}
          onClick={handleSubmit}
        >
          {isBuildiumRefund ? 'Record refund in Hudson' : 'Issue payment refund'}
        </Button>
      </HStack>
    </Stack>
  )
}

export default IssuePaymentRefundForm
